import axios from "axios";

// Use relative path so API calls work on any domain (preview, deployed, custom domain)
const API_BASE = "/api";

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Attach Bearer token from localStorage to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem("access_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// On 401, clear token and redirect to login
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config.url?.includes("/auth/")) {
      localStorage.removeItem("access_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// Handle numeric input - only allow numbers and decimals, keep blank if empty
export const handleNumericInput = (value) => {
  // Allow empty string
  if (value === "" || value === null || value === undefined) return "";
  
  // Remove any non-numeric characters except decimal point
  const cleaned = value.toString().replace(/[^0-9.]/g, "");
  
  // Prevent multiple decimal points
  const parts = cleaned.split(".");
  if (parts.length > 2) {
    return parts[0] + "." + parts.slice(1).join("");
  }
  
  return cleaned;
};

// Parse numeric value for submission - convert empty to 0
export const parseNumericValue = (value) => {
  if (value === "" || value === null || value === undefined) return 0;
  // Remove commas before parsing
  const cleaned = value.toString().replace(/,/g, "");
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

// Format number with Indian commas (without currency symbol) for input display
export const formatIndianNumber = (amount) => {
  if (amount === "" || amount === null || amount === undefined) return "";
  const num = parseFloat(amount.toString().replace(/,/g, ""));
  if (isNaN(num)) return "";
  
  return new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

// Format currency in INR with Indian accounting format (e.g., 1,00,000.00)
export const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

// Format date
export const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

// Dashboard
export const getDashboardSummary = (company = "all", month = "") => api.get(`/dashboard/summary?company=${company}${month ? `&month=${month}` : ""}`);

// Bank Accounts
export const getBankAccounts = () => api.get("/bank-accounts");
export const getBankTransactions = (accountId) => api.get(`/bank-accounts/${accountId}/transactions`);
export const getCreditCards = () => api.get("/credit-cards");
export const createBankAccount = (data) => api.post("/bank-accounts", data);
export const updateBankAccount = (id, data) => api.put(`/bank-accounts/${id}`, data);
export const deleteBankAccount = (id) => api.delete(`/bank-accounts/${id}`);

// Invoices
export const getInvoices = () => api.get("/invoices");
export const createInvoice = (data) => api.post("/invoices", data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const recordInvoicePayment = (id, amount, bankAccountId) => 
  api.post(`/invoices/${id}/receive`, { amount, bank_account_id: bankAccountId });
export const resetInvoicePayment = (id) => api.post(`/invoices/${id}/reset`);

// Payables
export const getPayables = () => api.get("/payables");
export const createPayable = (data) => api.post("/payables", data);
export const updatePayable = (id, data) => api.put(`/payables/${id}`, data);
export const deletePayable = (id) => api.delete(`/payables/${id}`);
export const recordPayablePayment = (id, amount, bankAccountId, projectAlloc = null, paidDate = "") => 
  api.post(`/payables/${id}/pay`, { amount, bank_account_id: bankAccountId, ...(projectAlloc || {}), ...(paidDate ? { paid_date: paidDate } : {}) });
export const resetPayablePayment = (id) => api.post(`/payables/${id}/reset`);

// Fixed Expenses (Master List)
export const getFixedExpenses = (month = "") => api.get(`/fixed-expenses${month ? `?month=${month}` : ""}`);
export const createFixedExpense = (data) => api.post("/fixed-expenses", data);
export const updateFixedExpense = (id, data) => api.put(`/fixed-expenses/${id}`, data);
export const deleteFixedExpense = (id, fromMonth = "") => api.delete(`/fixed-expenses/${id}${fromMonth ? `?from_month=${fromMonth}` : ""}`);

// Expense Transactions (Monthly Pending-to-Paid)
export const getExpenseTransactions = (month) => api.get(`/expense-transactions${month ? `?month=${month}` : ''}`);
export const getActionItems = () => api.get("/expense-transactions/action-items");
export const generateMonthlyTransactions = (month) => api.post(`/expense-transactions/generate?month=${month}`);
export const updateExpenseTransaction = (id, data) => api.put(`/expense-transactions/${id}`, data);
export const markTransactionPaid = (id, data) => api.post(`/expense-transactions/${id}/mark-paid`, data);
export const revertToPending = (id) => api.post(`/expense-transactions/${id}/revert-to-pending`);
export const deleteExpenseTransaction = (id) => api.delete(`/expense-transactions/${id}`);

// One-time Expenses
export const getOneTimeExpenses = () => api.get("/onetime-expenses");
export const createOneTimeExpense = (data) => api.post("/onetime-expenses", data);
export const updateOneTimeExpense = (id, data) => api.put(`/onetime-expenses/${id}`, data);
export const deleteOneTimeExpense = (id) => api.delete(`/onetime-expenses/${id}`);
export const recordOneTimeExpensePayment = (id, amount, bankAccountId, projectAlloc = null, paidDate = "") =>
  api.post(`/onetime-expenses/${id}/pay`, { amount, bank_account_id: bankAccountId, ...(projectAlloc || {}), ...(paidDate ? { paid_date: paidDate } : {}) });
export const resetOneTimeExpensePayment = (id) => api.post(`/onetime-expenses/${id}/reset`);

// Upload
export const uploadFile = (dataType, file) => {
  const formData = new FormData();
  formData.append("file", file);
  return api.post(`/upload/${dataType}`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
};

// Export
export const exportData = (dataType) => {
  return `${API_BASE}/export/${dataType}`;
};

// Dashboard Export
export const exportDashboardSummary = async (params) => {
  const response = await api.post("/dashboard/export", params, {
    responseType: 'blob'
  });
  return response;
};

// Auth
export const login = (data) => api.post("/auth/login", data, { withCredentials: true });
export const register = (data) => api.post("/auth/register", data);
export const logout = () => api.post("/auth/logout", {}, { withCredentials: true });
export const getCurrentUser = () => api.get("/auth/me", { withCredentials: true });
export const refreshToken = () => api.post("/auth/refresh", {}, { withCredentials: true });

// Estimated Inflows
export const getEstimatedInflows = (company = "all", status = "all") => 
  api.get(`/estimated-inflows?company=${company}&status=${status}`);
export const createEstimatedInflow = (data) => api.post("/estimated-inflows", data);
export const updateEstimatedInflow = (id, data) => api.put(`/estimated-inflows/${id}`, data);
export const deleteEstimatedInflow = (id) => api.delete(`/estimated-inflows/${id}`);
export const updateEstimatedInflowStatus = (id, status) => 
  api.patch(`/estimated-inflows/${id}/status?status=${status}`);
export const convertInflowToInvoice = (id, data = {}) => api.post(`/estimated-inflows/${id}/convert-to-invoice`, data);
export const getEstimatedInflowsSummary = (company = "all") => 
  api.get(`/estimated-inflows/summary/totals?company=${company}`);

// Analytics
export const getAnalyticsTrends = (company = "all", months = 12) =>
  api.get(`/analytics/trends?company=${company}&months=${months}`);

// User Management (Admin only)
export const getUsers = () => api.get("/users");
export const createUser = (data) => api.post("/users", data);
export const updateUser = (id, data) => api.put(`/users/${id}`, data);
export const deleteUser = (id) => api.delete(`/users/${id}`);

// Alerts & Email
export const previewAlerts = (daysAhead = 7) => api.get(`/alerts/preview?days_ahead=${daysAhead}`);
export const sendAlerts = (data) => api.post("/alerts/send", data);
export const sendTestEmail = (toEmail) => api.post("/alerts/test", { to_email: toEmail });

// Statutory (TDS/GST)
export const generateStatutory = () => api.post("/statutory/generate");
export const getStatutorySummary = () => api.get("/statutory/summary");

// Projects
export const getProjects = (company = "all") => api.get(`/projects?company=${company}`);
export const createProject = (data) => api.post("/projects", data);
export const getProject = (id) => api.get(`/projects/${id}`);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const addLineItem = (projectId, data) => api.post(`/projects/${projectId}/line-items`, data);
export const updateLineItem = (projectId, itemId, data) => api.put(`/projects/${projectId}/line-items/${itemId}`, data);
export const deleteLineItem = (projectId, itemId) => api.delete(`/projects/${projectId}/line-items/${itemId}`);
export const recordProjectSpend = (projectId, data) => api.post(`/projects/${projectId}/spend`, data);
export const updateSpendEntry = (projectId, itemId, spendIndex, data) => api.put(`/projects/${projectId}/line-items/${itemId}/spend/${spendIndex}`, data);
export const deleteSpendEntry = (projectId, itemId, spendIndex) => api.delete(`/projects/${projectId}/line-items/${itemId}/spend/${spendIndex}`);
export const getProjectSummary = (company = "all") => api.get(`/projects/summary?company=${company}`);
export const getStatutoryMonthly = (company = "all") => api.get(`/statutory/monthly-breakdown?company=${company}`);
export const toggleStatutoryPaid = (type, month, company = "all", bankAccountId = "") => api.post(`/statutory/monthly-toggle?type=${type}&month=${month}&company=${company}${bankAccountId ? `&bank_account_id=${bankAccountId}` : ""}`);

// Employee Advances / Loans
export const getEmployeeAdvances = (status = "", company = "") =>
  api.get(`/employee-advances?${status ? `status=${status}` : ""}${company ? `&company=${company}` : ""}`);
export const createEmployeeAdvance = (data) => api.post("/employee-advances", data);
export const updateEmployeeAdvance = (id, data) => api.put(`/employee-advances/${id}`, data);
export const deleteEmployeeAdvance = (id) => api.delete(`/employee-advances/${id}`);
export const recordAdvanceRecovery = (id, data) => api.post(`/employee-advances/${id}/recover`, data);

export default api;
